<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { t } from '@nova/core';
import gateFeedService from '@satellite/services/gate-feed-service';

import type { ValidationRule } from '@satellite/composables/useCustomFieldValidation';

type SelectedItem = { name: string } | null;

const {
  warehouseId,
  dataMapper,
  fieldName,
  placeholder,
  label,
  modelValue = null,
  rules = [],
  maxlength = 8,
  fetcher
} = defineProps<{
  warehouseId: string;
  dataMapper: (item: Record<string, unknown>) => SelectedItem | undefined;
  fieldName: string;
  placeholder?: string;
  label?: string;
  modelValue?: SelectedItem;
  rules?: ValidationRule[];
  maxlength?: number;
  /** Optional HTTP fetcher. When omitted, uses gateFeedService (requires global axios). */
  fetcher?: (warehouseId: string, signal?: AbortSignal) => Promise<Record<string, unknown>[]>;
}>();

const emit = defineEmits<{
  'update:model-value': [value: SelectedItem];
}>();

const items = ref<SelectedItem[]>([]);
const searchValue = ref<string | undefined>(undefined);
const selectedItem = ref<SelectedItem>(null);
const isLoading = ref(false);
const autocompleteRef = ref<{ blur: () => void } | null>(null);
const abortController = ref<AbortController | null>(null);

watch(
  () => modelValue,
  value => {
    selectedItem.value = value?.name ? { name: value.name } : null;
  },
  { immediate: true }
);

const loadItems = async () => {
  abortController.value?.abort();
  abortController.value = new AbortController();
  const { signal } = abortController.value;

  isLoading.value = true;
  try {
    const raw = fetcher
      ? await fetcher(warehouseId, signal)
      : await gateFeedService.getAutocomplete(warehouseId, { requestConfig: { signal } });
    const data: Record<string, unknown>[] = Array.isArray(raw) ? raw : [];
    items.value = data.map(dataMapper).filter((i): i is SelectedItem => i !== undefined);
  } catch (err) {
    if (signal.aborted) {
      return;
    }
    console.error('[SmartGateAutocomplete][loadItems] failed to load items', {
      warehouseId,
      error: err
    });
    items.value = [];
  } finally {
    if (!signal.aborted) {
      isLoading.value = false;
    }
  }
};

onUnmounted(() => abortController.value?.abort());

const addCustomItem = () => {
  const typed = searchValue.value?.trim();
  if (typed) {
    selectedItem.value = { name: typed };
    autocompleteRef.value?.blur();
    emit('update:model-value', selectedItem.value);
  }
};

const onBlur = () => {
  const typed = searchValue.value?.trim();
  if (typed && typed !== selectedItem.value?.name) {
    addCustomItem();
  }
};

const hasSearchWithResults = computed(
  () => !isLoading.value && Boolean(searchValue.value?.length) && items.value.length > 0
);

onMounted(loadItems);
</script>

<template>
  <v-autocomplete
    ref="autocompleteRef"
    color="primary"
    hide-details="auto"
    v-model="selectedItem"
    v-model:search="searchValue"
    :items="items"
    :placeholder="placeholder"
    :label="label"
    :loading="isLoading"
    :rules="rules"
    :maxlength="maxlength"
    item-title="name"
    return-object
    clearable
    variant="outlined"
    @update:model-value="emit('update:model-value', selectedItem)"
    @blur="onBlur">
    <template v-if="hasSearchWithResults" #prepend-item>
      <v-list-item role="option" class="smartgate-custom-item py-2" @click="addCustomItem">
        <v-list-item-title>
          {{ t('smartGate.selectAsField', { value: searchValue, fieldName }) }}
        </v-list-item-title>
      </v-list-item>
      <v-divider />
    </template>

    <template #no-data>
      <v-list-item
        v-if="!isLoading && searchValue?.length && !items.length"
        role="option"
        class="smartgate-custom-item py-2"
        @click="addCustomItem">
        <v-list-item-title>
          {{ t('smartGate.selectAsField', { value: searchValue, fieldName }) }}
        </v-list-item-title>
      </v-list-item>
      <v-list-item v-else>
        {{ t('smartGate.typeToSearchField', { fieldName }) }}
      </v-list-item>
    </template>
  </v-autocomplete>
</template>

<style scoped lang="scss">
.smartgate-custom-item {
  position: sticky;
  top: -10px;
  z-index: 2;
  background-color: $color-background-primary;
}
</style>
